import { useEffect, useMemo, useState } from "react"

import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import DashboardLayout from "@/layouts/DashboardLayout"
import { supabase } from "@/lib/supabase"

import { Constants } from "../../database.types"

// Derived from the generated enums, so a permission added by migration shows up here without a second edit.
const ROLES = Constants.public.Enums.app_role

type Role = (typeof ROLES)[number]

const ALL_PERMISSIONS = Constants.public.Enums.role_permission

type Permission = (typeof ALL_PERMISSIONS)[number]

const prettyLabel = (perm: string) => perm.replace(/_/g, " ")

const extractTable = (perm: string) => perm.split(".").slice(-1)[0]

const ALL_TABLES = Array.from(new Set(ALL_PERMISSIONS.map(extractTable))).sort()

export default function RolePermissions() {
  const [role, setRole] = useState<Role | "">("")
  const [table, setTable] = useState<string>("")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [existing, setExisting] = useState<Set<Permission>>(new Set())
  const [pending, setPending] = useState<Set<Permission>>(new Set())

  const tablePermissions: Permission[] = useMemo(
    () => ALL_PERMISSIONS.filter((p) => extractTable(p) === table),
    [table],
  )

  // Reset state when role or table changes
  useEffect(() => {
    setExisting(new Set())
    setError(null)
    if (!role || !table) return

    const fetchExisting = async () => {
      setLoading(true)
      setError(null)
      const allowedForTable = ALL_PERMISSIONS.filter((p) => extractTable(p) === table)
      const { data, error: err } = await supabase
        .from("role_permissions")
        .select("permission")
        .eq("role", role)
        .in("permission", allowedForTable)

      if (err) {
        console.error(err)
        setError(err.message)
        setLoading(false)
        return
      }

      const perms = new Set<Permission>((data || []).map((d) => d.permission as Permission))
      setExisting(perms)
      setLoading(false)
    }

    fetchExisting()
  }, [role, table])

  const togglePermission = async (perm: Permission) => {
    if (!role || !table) return
    if (pending.has(perm)) return
    setError(null)
    setPending((prev) => new Set(prev).add(perm))
    try {
      if (existing.has(perm)) {
        const { error: dErr } = await supabase
          .from("role_permissions")
          .delete()
          .eq("role", role)
          .eq("permission", perm)
        if (dErr) throw dErr
        setExisting((prev) => {
          const next = new Set(prev)
          next.delete(perm)
          return next
        })
      } else {
        const { error: iErr } = await supabase
          .from("role_permissions")
          .insert({ role, permission: perm })
        if (iErr) throw iErr
        setExisting((prev) => {
          const next = new Set(prev)
          next.add(perm)
          return next
        })
      }
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e)
      setError(msg || "Failed to update permission")
    } finally {
      setPending((prev) => {
        const next = new Set(prev)
        next.delete(perm)
        return next
      })
    }
  }

  return (
    <DashboardLayout>
      <Card>
        <CardHeader>
          <CardTitle>Role Permissions Editor</CardTitle>
          <CardDescription>
            Hardcoded roles and permissions. Uses browser Supabase client to read/write the{" "}
            <code>role_permissions</code> table. Choose a role and a table from the left columns.
            Toggling a permission immediately inserts/deletes rows.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="mb-4 p-3 rounded border border-red-300 bg-red-50 text-red-700">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Column 1: Roles */}
            <Card>
              <CardHeader className="py-3">
                <CardTitle className="text-base">Roles</CardTitle>
              </CardHeader>
              <CardContent className="pt-3">
                <ul className="space-y-2">
                  {ROLES.map((r) => (
                    <li key={r}>
                      <Button
                        variant={role === r ? "default" : "outline"}
                        size="sm"
                        className="w-full justify-start"
                        onClick={() => setRole(r)}
                      >
                        {r}
                      </Button>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Column 2: Tables */}
            <Card>
              <CardHeader className="py-3">
                <CardTitle className="text-base">Tables</CardTitle>
              </CardHeader>
              <CardContent className="pt-3">
                <ul className="space-y-2">
                  {ALL_TABLES.map((t) => (
                    <li key={t}>
                      <Button
                        variant={table === t ? "default" : "outline"}
                        size="sm"
                        className="w-full justify-start"
                        onClick={() => setTable(t)}
                      >
                        {t}
                      </Button>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Column 3: Permissions */}
            <Card>
              <CardHeader className="py-3">
                <CardTitle className="text-base">Permissions</CardTitle>
                <CardDescription>
                  {role && table
                    ? loading
                      ? "Loading…"
                      : `${Array.from(existing).filter((p) => extractTable(p) === table).length} enabled of ${tablePermissions.length}`
                    : "Select a role and a table"}
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-3">
                {role && table ? (
                  loading ? (
                    <div className="text-sm text-muted-foreground">Loading current selections…</div>
                  ) : (
                    <ul className="grid grid-cols-1 gap-2">
                      {tablePermissions.map((perm) => (
                        <li key={perm} className="flex items-center gap-2">
                          <Checkbox
                            id={perm}
                            checked={existing.has(perm)}
                            disabled={pending.has(perm)}
                            onCheckedChange={() => togglePermission(perm)}
                          />
                          <Label htmlFor={perm} className="text-sm">
                            {prettyLabel(perm)}
                            {pending.has(perm) && (
                              <span className="ml-2 text-xs text-muted-foreground">
                                (updating…)
                              </span>
                            )}
                          </Label>
                        </li>
                      ))}
                    </ul>
                  )
                ) : (
                  <div className="text-sm text-muted-foreground">Select a role and a table</div>
                )}
              </CardContent>
            </Card>
          </div>
        </CardContent>
      </Card>
    </DashboardLayout>
  )
}
